/**
 * BayLabelConfig Component
 *
 * Configuration controls for bay label font and size settings.
 * Provides a font family selector and font size slider/input for
 * customizing how bay numbers appear in the QR code center overlay.
 *
 * Requirements:
 * - 5.1: When center customization is enabled, display bay label configuration options
 * - 5.2: Include a font selector with choices: "Topgolf" and "ShieldSans"
 * - 5.3: Include a font size control with reasonable minimum and maximum limits
 * - 5.5: When a Super_Bay or Site_QR is being previewed, controls SHALL be disabled with explanatory text
 * - 5.6: Default the bay label font to "ShieldSans" and font size to a readable default value
 */

import { useCallback, useId } from 'react';
import type { FontFamily } from '../types';
import './BayLabelConfig.css';

/** Minimum font size in pixels */
export const MIN_FONT_SIZE = 12;

/** Maximum font size in pixels */
export const MAX_FONT_SIZE = 72;

/** Default font size in pixels (Requirement 5.6) */
export const DEFAULT_FONT_SIZE = 40;

/** Available font families for bay labels */
const FONT_OPTIONS: readonly { readonly value: FontFamily; readonly label: string }[] = [
  { value: 'ShieldSans', label: 'ShieldSans' },
  { value: 'Topgolf', label: 'Topgolf' },
] as const;

/**
 * Props for the BayLabelConfig component
 */
export interface BayLabelConfigProps {
  /** Current font family selection */
  readonly fontFamily: FontFamily;
  /** Current font size in pixels */
  readonly fontSize: number;
  /** Current font color in hex format */
  readonly fontColor: string;
  /** Whether the controls are disabled (for non-fixed bays) */
  readonly disabled: boolean;
  /** Reason why controls are disabled (shown as explanatory text) */
  readonly disabledReason?: string;
  /** Callback fired when font family changes */
  readonly onFontFamilyChange: (font: FontFamily) => void;
  /** Callback fired when font size changes */
  readonly onFontSizeChange: (size: number) => void;
  /** Callback fired when font color changes */
  readonly onFontColorChange: (color: string) => void;
}

/**
 * BayLabelConfig - Font and size settings for bay labels
 *
 * Provides controls for customizing the bay number label that appears
 * in the center overlay of fixed bay QR codes. Controls are disabled
 * when previewing Site QR or Super Bay codes since they don't have
 * bay numbers to display.
 *
 * @example
 * ```tsx
 * <BayLabelConfig
 *   fontFamily="ShieldSans"
 *   fontSize={24}
 *   disabled={false}
 *   onFontFamilyChange={(font) => setFontFamily(font)}
 *   onFontSizeChange={(size) => setFontSize(size)}
 * />
 * ```
 */
export function BayLabelConfig({
  fontFamily,
  fontSize,
  fontColor,
  disabled,
  disabledReason,
  onFontFamilyChange,
  onFontSizeChange,
  onFontColorChange,
}: BayLabelConfigProps): JSX.Element {
  const fontSelectId = useId();
  const fontSizeSliderId = useId();
  const fontSizeInputId = useId();
  const fontColorId = useId();

  const handleFontFamilyChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      onFontFamilyChange(event.target.value as FontFamily);
    },
    [onFontFamilyChange]
  );

  const handleFontSizeSliderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10);
      if (!isNaN(value)) {
        onFontSizeChange(clampFontSize(value));
      }
    },
    [onFontSizeChange]
  );

  const handleFontSizeInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10);
      if (!isNaN(value)) {
        onFontSizeChange(clampFontSize(value));
      }
    },
    [onFontSizeChange]
  );

  const handleFontSizeInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const value = parseInt(event.target.value, 10);
      if (isNaN(value)) {
        // Reset to current valid value if input is invalid
        onFontSizeChange(fontSize);
      } else {
        onFontSizeChange(clampFontSize(value));
      }
    },
    [fontSize, onFontSizeChange]
  );

  const handleFontColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFontColorChange(event.target.value);
    },
    [onFontColorChange]
  );

  return (
    <div className={`bay-label-config ${disabled ? 'bay-label-config--disabled' : ''}`}>
      <div className="bay-label-config__header">
        <h4 className="bay-label-config__title">Bay Label Settings</h4>
        {disabled && disabledReason && (
          <p className="bay-label-config__disabled-reason" role="status">
            {disabledReason}
          </p>
        )}
      </div>

      <div className="bay-label-config__controls">
        {/* Font Family Selector */}
        <div className="bay-label-config__field">
          <label className="bay-label-config__label" htmlFor={fontSelectId}>
            Font Family
          </label>
          <select
            id={fontSelectId}
            className="bay-label-config__select"
            value={fontFamily}
            onChange={handleFontFamilyChange}
            disabled={disabled}
            aria-describedby={disabled && disabledReason ? `${fontSelectId}-disabled` : undefined}
          >
            {FONT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Font Size Control */}
        <div className="bay-label-config__field">
          <label className="bay-label-config__label" htmlFor={fontSizeSliderId}>
            Font Size
          </label>
          <div className="bay-label-config__size-controls">
            <input
              id={fontSizeSliderId}
              type="range"
              className="bay-label-config__slider"
              min={MIN_FONT_SIZE}
              max={MAX_FONT_SIZE}
              value={fontSize}
              onChange={handleFontSizeSliderChange}
              disabled={disabled}
              aria-valuemin={MIN_FONT_SIZE}
              aria-valuemax={MAX_FONT_SIZE}
              aria-valuenow={fontSize}
            />
            <div className="bay-label-config__size-input-wrapper">
              <input
                id={fontSizeInputId}
                type="number"
                className="bay-label-config__size-input"
                min={MIN_FONT_SIZE}
                max={MAX_FONT_SIZE}
                value={fontSize}
                onChange={handleFontSizeInputChange}
                onBlur={handleFontSizeInputBlur}
                disabled={disabled}
                aria-label="Font size in pixels"
              />
              <span className="bay-label-config__size-unit" aria-hidden="true">
                px
              </span>
            </div>
          </div>
          <div className="bay-label-config__size-range" aria-hidden="true">
            <span>{MIN_FONT_SIZE}px</span>
            <span>{MAX_FONT_SIZE}px</span>
          </div>
        </div>

        {/* Font Color Control */}
        <div className="bay-label-config__field">
          <label className="bay-label-config__label" htmlFor={fontColorId}>
            Font Color
          </label>
          <div className="bay-label-config__color-controls">
            <input
              id={fontColorId}
              type="color"
              className="bay-label-config__color-input"
              value={fontColor}
              onChange={handleFontColorChange}
              disabled={disabled}
              aria-label="Bay label font color"
            />
            <span className="bay-label-config__color-value">{fontColor.toUpperCase()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Clamps a font size value to the valid range
 */
function clampFontSize(value: number): number {
  return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, value));
}
